import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';

export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    // If not authenticated but has refresh token, attempt to refresh
    if (authService.hasRefreshToken()) {
      return authService.refreshToken().pipe(
        tap(isValid => {
          if (!isValid) {
            // Use logout() — not just navigate — to clear user state and tokens.
            // Otherwise guestGuard still sees the user as authenticated and
            // redirects back, creating an infinite navigation loop.
            authService.logout();
          }
        })
      );
    }

    router.navigate(['/auth/login']);
    return false;
  }

  if (authService.isTokenExpired()) {
    return authService.refreshToken().pipe(
      tap(isValid => {
        if (!isValid) {
          authService.logout();
        }
      })
    );
  }

  if (authService.isDefaultRoleUser()) {
    router.navigate(['/unauthorized']);
    return false;
  }

  return true;
};

export const roleGuard = (roles: string[]): CanActivateFn => {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/auth/login']);
      return false;
    }

    if (!authService.hasAnyRole(roles)) {
      router.navigate(['/unauthorized']);
      return false;
    }

    return true;
  };
};

export const permissionGuard = (permissions: string[]): CanActivateFn => {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/auth/login']);
      return false;
    }

    const hasPermissions = permissions.every(permission => authService.hasPermission(permission));

    if (!hasPermissions) {
      router.navigate(['/unauthorized']);
      return false;
    }

    return true;
  };
};

export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    router.navigate([authService.getPostLoginRedirectRoute()]);
    return false;
  }

  return true;
};
